import React, { useEffect, useState } from "react";
import Button from "../../../ui/Button";
import ModalInput from "../../../ui/ModalTextInput";
import Dropdown from "../../../ui/SelectDropdown"; // Your Dropdown component
import CustomModalContainer from "../../../../components/modals/CustomModalContainer";
import { useAppDispatch, useAppSelector } from "../../../../redux/app/hooks";
import { RootState } from "../../../../redux/app/store";
import { mapDataToLabelValue } from "../../../../helpers";
import { validationSchema } from "./schemas";
import { fetchCities, fetchStates } from "../../../../redux/features/utilSlice";
import { useParams } from "react-router-dom";
import {
  addCourierOffice,
  fetchCourierOffice,
} from "../../../../redux/features/CourrierSlice";
import { toast } from "react-toastify";

interface AddCourierBranchProps {
  open: boolean;
  onClose: () => void;
}

const AddCourierBranch: React.FC<AddCourierBranchProps> = ({
  open,
  onClose,
}) => {
  const { countries, states, cities } = useAppSelector(
    (state: RootState) => state.util
  );
  const { loading } = useAppSelector((state) => state.courier);
  const dispatch = useAppDispatch();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    name: "",
    address: "",
    cityId: "",
    stateId: "",
    countryId: "",
    latitude: "",
    longitude: "",
    courierId: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    address: "",
    cityId: "",
    stateId: "",
    countryId: "",
    latitude: "",
    longitude: "",
    courierId: "",
  });

  const validateForm = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      return true;
    } catch (err: any) {
      const validationErrors: any = {};
      err.inner.forEach((error: any) => {
        validationErrors[error.path] = error.message;
      });
      setErrors(validationErrors);
      return false;
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleDiscard = () => {
    setFormData({
      name: "",
      address: "",
      cityId: "",
      stateId: "",
      countryId: "",
      latitude: "",
      longitude: "",
      courierId: "",
    });
    setErrors({
      name: "",
      address: "",
      cityId: "",
      stateId: "",
      countryId: "",
      latitude: "",
      longitude: "",
      courierId: "",
    });
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const isValid = await validateForm();
    if (isValid) {
      try {
        await dispatch(addCourierOffice({ ...formData, courierId: id }))
          .unwrap()
          .then(() => {
            handleDiscard();
            dispatch(fetchCourierOffice({ courierId: id }));
          });
      } catch (error: any) {
        toast.error(
          error?.message ?? "An error occurred while adding courier office:",
          error
        );
      }
    }
  };

  useEffect(() => {
    formData?.countryId && dispatch(fetchStates(formData?.countryId));
  }, [formData?.countryId, dispatch]);

  useEffect(() => {
    formData?.countryId &&
      formData?.stateId &&
      dispatch(
        fetchCities({
          countryId: formData?.countryId,
          stateId: formData?.stateId,
        })
      );
  }, [formData?.countryId, formData?.stateId, dispatch]);

  if (!open) return null; // Do not render if modal is closed

  return (
    <CustomModalContainer
      title="Add Branch"
      footer={
        <div className="flex justify-between relative bottom-0">
          <Button
            type="button"
            onClick={handleDiscard}
            label="Discard"
            layout="inline" variant="ghost"
          />
          <Button
            label="Done"
            type="submit"
            layout="inline"
            onClick={handleSubmit}
            isLoading={loading}
          />
        </div>
      }
      open={open}
      onClose={handleDiscard}
    >
      {/* Modal Body */}
      <div className="p-6">
        <form onSubmit={handleSubmit}>
          <ModalInput
            label="Name of branch"
            placeholder="Enter name of branch"
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={errors.name}
            required
          />
          <ModalInput
            label="Address"
            placeholder="Enter address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            error={errors.address}
            required
          />
          <ModalInput
            label="Longitude"
            placeholder="Enter longitude"
            name="longitude"
            value={formData.longitude}
            onChange={handleChange}
            error={errors.longitude}
            required
          />
          <ModalInput
            label="Latitude"
            placeholder="Enter latitude"
            name="latitude"
            value={formData.latitude}
            onChange={handleChange}
            error={errors.latitude}
            required
          />
          <Dropdown
            label="Country"
            name="countryId"
            options={mapDataToLabelValue(countries, "name", "id")}
            placeholder="Select country"
            value={formData.countryId}
            onSelect={(value) => handleSelectChange("countryId", value)} // Handle selection change
            error={errors.countryId}
            required
          />
          <Dropdown
            label="State"
            name="stateId"
            options={mapDataToLabelValue(states, "name", "id")}
            placeholder="Select state"
            value={formData.stateId}
            onSelect={(value) => handleSelectChange("stateId", value)} // Handle selection change
            error={errors.stateId}
            required
          />
          <Dropdown
            label="City"
            name="cityId"
            options={mapDataToLabelValue(cities, "name", "id")}
            placeholder="Select city"
            value={formData.cityId}
            onSelect={(value) => handleSelectChange("cityId", value)} // Handle selection change
            error={errors.cityId}
            required
          />
        </form>
      </div>
    </CustomModalContainer>
  );
};

export default AddCourierBranch;
